import { useCallback } from 'react';
import { EventType, useEventBus } from 'context';
import { SUPER_SEARCH_COMMANDS } from './SuperSearch.constants';
import type { SuperSearchDisplayItem } from './SuperSearch.types';

interface UseSuperSearchActionsOptions {
  onClose: () => void;
  onExecute: (item: SuperSearchDisplayItem) => void;
  onNavigate: (item: SuperSearchDisplayItem) => void;
  onScopeItem: (item: SuperSearchDisplayItem) => boolean;
  onStartTour: (tourId: string) => void;
}

const useSuperSearchActions = ({
  onClose,
  onExecute,
  onNavigate,
  onScopeItem,
  onStartTour,
}: UseSuperSearchActionsOptions) => {
  const { publish } = useEventBus();

  return useCallback(
    (item: SuperSearchDisplayItem) => {
      if (item.kind === 'company-page-command' || item.kind === 'firm-page-command') {
        onScopeItem(item);
        return;
      }

      switch (item.command) {
        case SUPER_SEARCH_COMMANDS.CREATE_COMPANY:
          onClose();
          publish(EventType.OPEN_CREATE_COMPANY_DIALOG, { allowEscapeClose: true });
          // Do not record history until the create flow succeeds; cancel/abandon must not train ranking.
          return;
        case SUPER_SEARCH_COMMANDS.CREATE_MEASUREMENT_DATE:
          onClose();
          publish(EventType.OPEN_CREATE_MEASUREMENT_DATE_DIALOG, { allowEscapeClose: true });
          // Do not record history until the create flow succeeds; cancel/abandon must not train ranking.
          return;
        case SUPER_SEARCH_COMMANDS.OPEN_NOTIFICATIONS:
          onClose();
          publish(EventType.OPEN_NOTIFICATIONS, undefined);
          onExecute(item);
          return;
        case SUPER_SEARCH_COMMANDS.START_GUIDED_TOUR:
          if (item.tourId) {
            onClose();
            onStartTour(item.tourId);
            onExecute(item);
            return;
          }
          break;
        default:
          break;
      }

      onNavigate(item);
    },
    [onClose, onExecute, onNavigate, onScopeItem, onStartTour, publish]
  );
};

export default useSuperSearchActions;
